import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { EmptyState } from "../EmptyState";
import { Bookmark, Trash2, ExternalLink } from "lucide-react";

function humanFilters(f = {}) {
  const parts = [];
  if (f.q) parts.push(`"${f.q}"`);
  if (f.district) parts.push(f.district);
  if (f.concelho) parts.push(f.concelho);
  if (f.category) parts.push(f.category);
  if (f.regime) parts.push(f.regime);
  if (f.work_mode) parts.push(f.work_mode);
  if (f.near_concelho) parts.push(`até ${f.radius_km || 25} km de ${f.near_concelho}`);
  if (f.sort && f.sort !== "recent") parts.push(f.sort === "salary" ? "melhor pagas" : "mais perto");
  return parts.join(" · ") || "Todas as vagas";
}

function savedSearchUrl(s) {
  const p = new URLSearchParams();
  Object.entries(s.filters || {}).forEach(([k, v]) => { if (v !== null && v !== "") p.set(k, String(v)); });
  return `/empregos?${p.toString()}`;
}

export const SavedSearchesTab = () => {
  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/candidate/saved-searches")
      .then((r) => setSavedSearches(r.data.saved_searches || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleAlert = async (s) => {
    try {
      const { data } = await api.patch(`/candidate/saved-searches/${s.id}`, { alert: !s.alert });
      setSavedSearches(savedSearches.map((x) => (x.id === s.id ? data : x)));
      toast.success(data.alert ? "Alertas ativados" : "Alertas desativados");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/candidate/saved-searches/${id}`);
      setSavedSearches(savedSearches.filter((s) => s.id !== id));
      toast.success("Pesquisa removida");
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="max-w-2xl">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1.5 mb-4">
        <Bookmark className="w-3.5 h-3.5 text-amber-500" /> Pesquisas guardadas
      </p>
      {loading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => <div key={i} className="h-16 bg-white rounded-xl border border-black/5 animate-pulse" />)}
        </div>
      ) : savedSearches.length === 0 ? (
        <EmptyState
          icon={Bookmark}
          title="Sem pesquisas guardadas"
          description="Na lista de vagas aplica filtros e clica em 'Guardar pesquisa' para a reabrires com um toque e receberes alertas."
          actionLabel="Explorar vagas"
          onAction={() => { window.location.href = "/empregos"; }}
        />
      ) : (
        <div className="space-y-3" data-testid="saved-searches-list">
          {savedSearches.map((s) => (
            <div key={s.id} data-testid={`saved-search-${s.id}`} className="bg-white rounded-xl border border-black/5 p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 truncate">{s.name}</p>
                <p className="text-xs text-slate-500 truncate">{humanFilters(s.filters || {})}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                  <Switch
                    data-testid={`saved-search-alert-${s.id}`}
                    checked={!!s.alert}
                    onCheckedChange={() => toggleAlert(s)}
                  />
                  Alertas
                </label>
                <Link
                  to={savedSearchUrl(s)}
                  data-testid={`saved-search-open-${s.id}`}
                  className="inline-flex items-center gap-1 rounded-full h-9 px-3.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold transition"
                >
                  Abrir <ExternalLink className="w-3 h-3" />
                </Link>
                <Button data-testid={`saved-search-delete-${s.id}`} size="sm" variant="ghost" className="rounded-full text-red-600 hover:text-red-700" onClick={() => remove(s.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedSearchesTab;
